import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useAuthenticationStatus } from '@nhost/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'components/Button';
import { SizeEnum } from 'utils';
import { GET_STORES } from 'gql/queries';
import { GET_GORGIAS_INSTALL_URL } from 'gql/mutations';
import {
  beginGorgiasAuthorization, canonicalGorgiasAccount, GORGIAS_INSTALL_RETURN_KEY,
  rememberGorgiasInstallation,
} from 'utils/gorgiasInstallation';

export const GorgiasMarketplaceInstall = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const accounts = new URLSearchParams(search).getAll('account');
  const account = accounts.length === 1 ? canonicalGorgiasAccount(accounts[0]) : null;
  const { isAuthenticated, isLoading: authLoading } = useAuthenticationStatus();
  const { data, loading, error: storesError } = useQuery(GET_STORES, {
    skip: !isAuthenticated || !account,
  });
  const [getInstallUrl, { loading: installLoading }] = useMutation(GET_GORGIAS_INSTALL_URL);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (account && !authLoading && !isAuthenticated) {
      rememberGorgiasInstallation(account);
      navigate('/login', { replace: true });
    }
  }, [account, authLoading, isAuthenticated, navigate]);

  const connect = async (storeId: string) => {
    if (!account || connecting || installLoading) return;
    setConnecting(true);
    setError('');
    try {
      const result = await getInstallUrl({ variables: { storeId, account } });
      beginGorgiasAuthorization(result.data?.gorgiasInstallUrl);
      localStorage.removeItem(GORGIAS_INSTALL_RETURN_KEY);
    } catch {
      setError('We could not connect this store. Check that you are a store admin or manager, then try again.');
    } finally {
      setConnecting(false);
    }
  };

  if (!account) return <div className="p-8" role="alert">Start from the Gorgias marketplace with a valid Gorgias account.</div>;
  if (authLoading || !isAuthenticated || loading) return <div className="p-8" role="status">Loading your stores…</div>;
  if (storesError) return <div className="p-8" role="alert">We could not load your stores. Reload this page to try again.</div>;
  const stores = (data?.stores || []).filter((store: any) => !store.uninstalledAt);

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-5 px-4 py-12">
      <div className="w-full max-w-md space-y-4">
        <h1 className="text-xl font-semibold text-[#0b1c30]">Connect Gorgias to ShipAid</h1>
        <p className="text-sm text-[#64748b]">Choose the store to connect to {account}.gorgias.com. You must be a store admin or manager.</p>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        {!stores.length ? <>
          <h2 className="font-semibold">No ShipAid store found</h2>
          <p className="text-sm">Install ShipAid on your Shopify store before connecting Gorgias.</p>
          <Link className="text-shipaidBlue" to="/">Open ShipAid</Link>
        </> : stores.map((store: any) => (
          <Button key={store.id} onClick={() => connect(store.id)} disabled={connecting || installLoading}
            size={SizeEnum.md} color="primary" variant="outlined" className="w-full justify-center">
            Connect {store.name || store.store}
          </Button>
        ))}
      </div>
    </main>
  );
};
